import { Injectable } from '@angular/core';
import { BaseService, ConfigService, LoggingService, PITEventService, UtilsMisc, UtilsString } from '@pit/core';
import { PinSignalRClientService } from '@pit/core-signalr';
import { IDtoDevice, IDtoUser } from '@pit/frontend-model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PinItTrackFrontendDeviceDialogComponent } from '../components/pin.it.track.frontend.device.dialog/pin.it.track.frontend.device.dialog.component';
import { PinItTrackFrontendMandatorAssignDialogComponent } from '../components/pin.it.track.frontend.mandator.assign.dialog/pin.it.track.frontend.mandator.assign.dialog.component';
import { PinItTrackFrontendMessageDialogComponent } from '../components/pin.it.track.frontend.message.dialog/pin.it.track.frontend.message.dialog.component';
import { PinItTrackFrontendUserDialogComponent } from '../components/pin.it.track.frontend.user.dialog/pin.it.track.frontend.user.dialog.component';
import { AppMode } from '../module.core/services/interfaces/pin.it.track.frontend.application.service.interface';
import { PinItTrackFrontendDataServicesAccessor } from '../module.data/accessor/implementations/pin.it.track.frontend.data.services.accessor';
import { PinItTrackFrontendEntityStoreFacadesAccessor } from '../module.store/accessor/pin.it.track.frontend.entity.store.facades.accessor';
import { PinItTrackFrontendEventSignalRHandler } from './pin.it.track.frontend.event.signalr.handler';
import { EventApplication, EventUI } from './pin.it.track.frontend.events';
import { PinItTrackFrontendUserDeviceDialogComponent } from '../components/pin.it.track.frontend.user.device.dialog/pin.it.track.frontend.user.device.dialog.component';

@Injectable({
    providedIn: 'root'
})
export class PinItTrackFrontendEventHandlerService extends BaseService {

    private signalRHandler: PinItTrackFrontendEventSignalRHandler = null;

    constructor(configurationService: ConfigService, loggingService: LoggingService, private dataServicesAccessor: PinItTrackFrontendDataServicesAccessor,
        private entityStoreFacadesAccessor: PinItTrackFrontendEntityStoreFacadesAccessor, private eventService: PITEventService, private signalRService: PinSignalRClientService,
        private dialogService: DialogService) {
        super(configurationService, loggingService, false);
    }

    protected override async doInitializeAsync(): Promise<void> {
        if (this.isInitialized === false) {
            this.eventService.subscribeForEvent(EventApplication.AppInit, this.handleEventAppInitAsync.bind(this));

            this.eventService.subscribeForEvent(EventUI.NavigateToLogin, this.handleEventNavigatoToLoginAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToLogout, this.handleEventNavigateToLogoutAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToPasswordReset, this.handleEventNavigateToPasswordResetAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToHome, this.handleEventNavigateToHomeAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToDepotList, this.handleEventNavigateToDepotListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToDeviceList, this.handleEventNavigateToDeviceListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToResourceList, this.handleEventNavigateToResourceListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToPostmanList, this.handleEventNavigateToPostmanListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToShipmentList, this.handleEventNavigateToShipmentListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToShipmentReturns, this.handleEventNavigateToShipmentReturnsAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToShipmentLost, this.handleEventNavigateToShipmentLostAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToDeviceUserList, this.handleEventNavigateToDeviceUserListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToWebUserList, this.handleEventNavigateToWebUserListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToVersionList, this.handleEventNavigateToVersionListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToSettingList, this.handleEventNavigateToSettingListAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToDeviceUserDialog, this.handleEventNavigateToDeviceUserDialogAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToWebUserDialog, this.handleEventNavigateToWebUserDialogAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToMessageDialog, this.handleEventNavigateToMessageDialogAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToDeviceDialog, this.handleEventNavigateToDeviceDialogAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToMandatorAssignDialog, this.handleEventNavigateToMandatorAssignDialogAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToShipmentsBookToDepot, this.handleEventNavigateToShipmentsBookToDepotAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToLocationMap, this.handleEventNavigateToLocationMapAsync.bind(this));
            this.eventService.subscribeForEvent(EventUI.NavigateToTour, this.handleEventNavigateToTourAsync.bind(this));

            this.signalRHandler = new PinItTrackFrontendEventSignalRHandler(this.configurationService, this.dataServicesAccessor, this.entityStoreFacadesAccessor, this.signalRService, this.eventService);
            this.signalRHandler.initializeAsync();
        }
    }

    private async handleEventAppInitAsync(data: any): Promise<void> {
        try {
            this.eventService.useMethod('busy.on');

            if (UtilsMisc.isNullOrUndefined(data) === true) {
                data = {
                    mode: AppMode.User
                };
            }

            this.eventService.sendEvent('application', {
                type: 'init',
                mode: data.mode
            });

            if (data.mode !== AppMode.Guest) {
                await this.entityStoreFacadesAccessor.shipmentReturnCodeStoreFacade.loadItemsAsync();
                await this.entityStoreFacadesAccessor.depotStoreFacade.loadDepotsForMandatorAsync(this.entityStoreFacadesAccessor.currentUser.mandator.id);
                await this.signalRHandler.startAsync();
            }
        } catch (error) {
            this.loggingService.error(error);
        } finally {
            this.eventService.useMethod('busy.off');
        }

        await this.handleEventNavigateToHomeAsync();
    }

    public async handleEventNavigateToShipmentsBookToDepotAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'shipment/book/depot');
    }

    private async handleEventNavigatoToLoginAsync(): Promise<void> {
        this.eventService.sendEvent('auth', {
            type: 'login.credentials.start'
        });
    }

    private async handleEventNavigateToLogoutAsync(): Promise<void> {
        await this.signalRHandler.stopAsync();

        this.eventService.sendEvent('auth', {
            type: 'logout'
        });
    }

    private async handleEventNavigateToPasswordResetAsync(): Promise<void> {
        this.eventService.sendEvent('auth', {
            type: 'login.password.reset.start'
        });
    }

    private async handleEventNavigateToHomeAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', '/home');
    }

    private async handleEventNavigateToDepotListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'depot/list');
    }

    private async handleEventNavigateToDeviceListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'device/list');
    }

    private async handleEventNavigateToResourceListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'resource/list');
    }

    private async handleEventNavigateToPostmanListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'postman/list');
    }

    private async handleEventNavigateToShipmentListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'shipment/list');
    }

    private async handleEventNavigateToShipmentReturnsAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'shipment/returns');
    }

    private async handleEventNavigateToShipmentLostAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'shipment/lost');
    }

    private async handleEventNavigateToDeviceUserListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'user/listdevice');
    }

    private async handleEventNavigateToWebUserListAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'user/listweb');
    }

    private async handleEventNavigateToVersionListAsync(): Promise<void> {
        if (this.dataServicesAccessor.currentUser?.isAdmin === true) {
            this.eventService.sendEvent('navigate', 'version/list');
        }
    }

    private async handleEventNavigateToSettingListAsync(): Promise<void> {
        if (this.dataServicesAccessor.currentUser?.isAdmin === true) {
            this.eventService.sendEvent('navigate', 'setting/list');
        }
    }

    public async handleEventNavigateToLocationMapAsync(): Promise<void> {
        if (this.dataServicesAccessor.currentUser?.isAdmin === true) {
            this.eventService.sendEvent('navigate', 'location/map');
        }
    }

    private async handleEventNavigateToTourAsync(): Promise<void> {
        this.eventService.sendEvent('navigate', 'tour/tour.show');
    }

    private async handleEventNavigateToDeviceUserDialogAsync(user: IDtoUser): Promise<void> {
        this.dialogService.open(PinItTrackFrontendUserDeviceDialogComponent, {
            data: {
                user: user
            },
            header: ((user?.id === null) ? 'NEUER GERÄTE-BENUTZER' : 'GERÄTE-BENUTZER BEARBEITEN')
        });
    }

    private async handleEventNavigateToWebUserDialogAsync(user: IDtoUser): Promise<void> {
        this.dialogService.open(PinItTrackFrontendUserDialogComponent, {
            data: {
                user: user
            },
            header: ((user?.id === null) ? 'NEUER WEB-BENUTZER' : 'WEB-BENUTZER BEARBEITEN')
        });
    }

    private async handleEventNavigateToDeviceDialogAsync(device: IDtoDevice): Promise<void> {
        this.dialogService.open(PinItTrackFrontendDeviceDialogComponent, {
            data: {
                device: device
            },
            header: ((device?.id === null) ? 'NEUES GERÄT' : 'GERÄT BEARBEITEN')
        });
    }

    private async handleEventNavigateToMandatorAssignDialogAsync(data: any): Promise<void> {
        const device: IDtoDevice = data?.device;
        const ref: DynamicDialogRef = this.dialogService.open(PinItTrackFrontendMandatorAssignDialogComponent, {
            data: {
                device: device
            },
            header: 'MANDATOR BEAUFTRAGEN'
        });
        ref.onClose.subscribe(async (updatedDevice: IDtoDevice) => {
            if ((UtilsMisc.isNullOrUndefined(updatedDevice) === false) && (UtilsMisc.isNullOrUndefined(data?.callback) === false)) {
                await data.callback(updatedDevice);
            }
        });
    }

    private async handleEventNavigateToMessageDialogAsync(data: any): Promise<void> {
        const ref: DynamicDialogRef = this.dialogService.open(PinItTrackFrontendMessageDialogComponent, {
            header: 'NACHRICHT'
        });
        ref.onClose.subscribe(async (message: string) => {
            if (UtilsString.stringIsNullOrEmpty(message) === false) {
                try {
                    this.eventService.useMethod('busy.on');
                    message = UtilsString.stringHtmlEscape(message).replace(/\n/g, '<br>')
                    switch (data.messageType) {
                        case 'devices': {
                            await this.dataServicesAccessor.communicationDataService.sendMessageToDevicesAsync(message, null, data.receivers);
                            break;
                        }
                        case 'depots': {
                            await this.dataServicesAccessor.communicationDataService.sendMessageToDepotsAsync(this.dataServicesAccessor.currentUser.mandator.code, message, null, data.receivers);
                            break;
                        }
                    }
                } catch (error) {
                } finally {
                    this.eventService.useMethod('busy.off');
                }
            }
        });
    }

}
